// Third-party
import round from 'lodash/round';

// Namespaces
import * as ProposalNS from '../../../Proposal';
import * as ProposalElementNS from '../../../ProposalElements';
import { ProposalStyleClassNames } from '../../../ProposalStyleClassNames';
import { DisclaimerConfig, ProposalDAOInterface } from '../../ProposalDAO';
import { BMOGIFBasedProposalDAO } from '../../ProposalDAOImpl';
import { ProposalFieldDAO } from '../../ProposalFieldDAO';
import { BMOGIFItemDAO } from '../../ProposalItemDAO';

// Utils
import {
  getDeathBenefitDisclaimer,
  scenarioHasWithdrawal,
  getMaturityGuaranteeDisclaimer,
  getSystematicInfo,
  getLumpSumInfo,
} from '../../../../../utils/bmo-gif';
import { BMOi18n, GIF_GRAPH_CONFIGS } from '../../../../../utils/bmo-gif/constants';
import { splitGifItemToPages } from '../../../../../utils/bmo-gif/utils';
import { PROPOSAL_DISCLAIMERS } from '../../../utils/constants';
import { CurrencyCell, NotApplicableCell, TextCell, ProposalFieldLabelCell } from '../../../../../views/components/cells';
import LineSeriesData from '@/types/line-series-data';
import LineSeriesDataPoint from '@/types/line-series-data-point';
import Timestamp from '@/types/timestamp';
import DashStyle from '@/types/dash-style';
import LocalizedString from '@/types/localized-string';
import BMOProposalData from '@/types/bmo-gif/bmo-proposal-data';

export default class BmoGifDAO extends BMOGIFBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new BmoGifDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  lineData: LineSeriesData[] = [];

  isGraph: boolean = false;

  isScenarioSummary: boolean = false;

  proposalData: BMOProposalData | undefined;

  protected build(): void {
    let pageIndex: number = 0;

    const daoConfig: ProposalNS.BmoGifConfig = this.proposalElement.DAO as ProposalNS.BmoGifConfig;
    if (daoConfig !== undefined) {
      pageIndex = daoConfig.pageIndex;
      this.isGraph = daoConfig.isGraph;
      this.isScenarioSummary = daoConfig.isScenarioSummary;
    }

    let itemDAO: BMOGIFItemDAO | null = this.nextItem();

    while (itemDAO) {
      const proposalDataList: BMOProposalData[] = splitGifItemToPages(itemDAO.item, this.proposal.hideBaseGifScenarios);
      this.proposalData = proposalDataList[pageIndex];

      if (this.isScenarioSummary) {
        const { projectionBasis, equityPercent } = this.proposalData.portfolio;

        let fundReturn: string = '';

        if (projectionBasis && equityPercent !== undefined) {
          const equity = `${round(equityPercent * 100, 2)}% ${BMOi18n[this.locale].PROPOSAL.EQUITY}`;
          const bond = `${round(100 - equityPercent * 100, 2)}% ${BMOi18n[this.locale].PROPOSAL.BOND}`;

          fundReturn = `${BMOi18n[this.locale][projectionBasis.name]} (${equity} ${bond})`;
        }

        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.FUND_RETURN, fundReturn, TextCell, 0);

        // Base scenario has no number
        if (!this.proposalData.scenarioNumber) {
          this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.WITHDRAWAL_AMOUNT, BMOi18n[this.locale].PROPOSAL.NONE, TextCell, 0);
        } else {
          this.addItemField(
            itemDAO,
            BMOi18n[this.locale].PROPOSAL.HEADERS.LUMP_SUM_WITHDRAWAL_AMOUNT,
            getLumpSumInfo(this.proposalData.scenario, this.locale),
            TextCell,
            0,
          );

          this.addItemField(
            itemDAO,
            BMOi18n[this.locale].PROPOSAL.HEADERS.SYSTEMATIC_WITHDRAWAL_AMOUNT,
            getSystematicInfo(this.proposalData.scenario, itemDAO.item, this.locale),
            TextCell,
            0,
          );
        }
      } else if (!this.isGraph) {
        const page = this.proposalData.pageData;
        for (const datum of page) {
          this.addItemField(itemDAO, '', this.formatDate(datum.date, 'll'), TextCell, 0);
          this.addItemField(itemDAO, '', datum.age, TextCell, 1);
          this.addItemField(itemDAO, '', { value: datum.portfolioValue }, CurrencyCell, 2);
          this.addItemField(itemDAO, '', { value: datum.netWithdrawal }, CurrencyCell, 3);
          this.addItemField(itemDAO, '', { value: datum.netDeposit }, CurrencyCell, 4);
          this.addItemField(itemDAO, '', { value: datum.maturityGuarantee }, CurrencyCell, 5);
          this.addItemField(itemDAO, '', { value: datum.deathBenefitGuarantee }, CurrencyCell, 6);
        }
      } else {
        const { scenario } = this.proposalData;
        const isAdverse: boolean = this.proposalData.isAdverse !== undefined ? this.proposalData.isAdverse : false;
        const data = isAdverse ? scenario.adverseData : scenario.data;
        const graphConfigs = Object.values(GIF_GRAPH_CONFIGS);

        for (const config of graphConfigs) {
          if (config.key === 'netWithdrawal') {
            config.hidden = !scenarioHasWithdrawal(scenario);
          }
          if (config.key === 'netDeposit') {
            config.hidden = scenario.deposit.amount === 0;
          }
        }

        for (const config of graphConfigs.filter(item => !item.hidden)) {
          const lineData = data.map(item => [new Date(item.date).getTime() as Timestamp, item[config.key]]) as LineSeriesDataPoint[];

          const lineChartData = {
            data: lineData,
            color: config.color,
            name: BMOi18n[this.locale].SCENARIOS[config.i18nKey],
            type: 'line',
            dashStyle: 'Solid' as DashStyle,
            lineWidth: 2,
            tooltip: config.tooltip && config.tooltip[this.locale],
          };
          this.lineData.push(lineChartData);
        }

        const withdrawal: boolean = scenario && !scenarioHasWithdrawal(scenario);
        const deposit: boolean = scenario.deposit.amount === 0;

        const maturityLabel = `${BMOi18n[this.locale].SCENARIOS.MATURITY_GUARANTEE}**`;
        const dbLabel = `${BMOi18n[this.locale].SCENARIOS.DEATH_BENEFIT_GUARANTEE}***`;
        const depositLabel = `${BMOi18n[this.locale].SCENARIOS.SUBSEQUENT_DEPOSIT}*`;

        const legendItems = [
          {
            label: BMOi18n[this.locale].SCENARIOS.PORTFOLIO_VALUE,
            color: GIF_GRAPH_CONFIGS.PORTFOLIO_VALUE.color,
          },
          {
            label: BMOi18n[this.locale].SCENARIOS.NET_WITHDRAWAL,
            color: GIF_GRAPH_CONFIGS.NET_WITHDRAWAL.color,
            hidden: withdrawal,
          },
          {
            label: depositLabel,
            color: GIF_GRAPH_CONFIGS.SUBSEQUENT_DEPOSIT.color,
            hidden: deposit,
          },
          {
            label: maturityLabel,
            color: GIF_GRAPH_CONFIGS.MATURITY_GUARANTEE.color,
          },
          {
            label: dbLabel,
            color: GIF_GRAPH_CONFIGS.DEATH_BENEFIT_GUARANTEE.color,
          },
        ].filter(item => !item.hidden);

        for (let i = 0; i < legendItems.length; i++) {
          const field: ProposalFieldDAO = new ProposalFieldDAO('', legendItems[i].label, ProposalStyleClassNames.ProposalTableContent);
          field.cellType = ProposalFieldLabelCell;
          field.color = legendItems[i].color;
          this.addFieldDAO(itemDAO, field, 0);
        }
      }

      itemDAO = this.nextItem();
    }
  }

  getDisclaimerConfigs(): Array<DisclaimerConfig> {
    if (this.isGraph && this.getItemDAOs().length > 0 && this.proposalData !== undefined) {
      const { item } = this.getItemDAOs()[0];
      const { planOption } = item.portfolio;
      const { scenario } = this.proposalData;

      const disclaimer1 = scenario.deposit.amount > 0 ? `*${BMOi18n[this.locale].GIF_GRAPH.DEPOSIT}` : '';
      const disclaimer2 = `**${getMaturityGuaranteeDisclaimer(this.locale, planOption)}`;
      const disclaimer3 = `***${getDeathBenefitDisclaimer(this.locale, planOption)}`;
      const disclaimer = `<div>${disclaimer1}</div> <div>${disclaimer2}</div> <div>${disclaimer3}</div>`;
      const text: LocalizedString = {
        'en-CA': disclaimer,
        'fr-CA': disclaimer,
      };

      return [
        {
          key: PROPOSAL_DISCLAIMERS.CUSTOM_DISCLAIMER,
          config: { customString: text, isHTML: true },
          items: [],
        },
      ];
    }

    return [];
  }

  getLineSeries(): LineSeriesData[] {
    return this.lineData;
  }

  private addItemField(itemDAO: BMOGIFItemDAO, name: string, value: any, cellType: any, index: number, cellFormat?: string): void {
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value != null ? value : '', ProposalStyleClassNames.ProposalTableContent);
    field.cellType = value != null ? cellType : NotApplicableCell;
    field.cellFormat = cellFormat;
    field.withSuffixes = false;
    field.decimalPlace = 0;
    this.addFieldDAO(itemDAO, field, index);
  }
}
